import { applySoundPack, applyVisualPack } from '../../assets/creatorPacks';
import type { BumperPart, TableDefinition, TargetPart } from './definition';
import { addCatchBonus } from './catchBonus';
import { normalizeBuiltInHardware } from './builtInHardware';
import { GRAND_CIRCUIT } from './grandCircuit';
import { rebuildTableFlow } from './flowLayouts';
import {
  addUpperFlippers,
  addUpperRamp,
  makeRampReadable,
  normalizePlayfieldShell,
  resizeTable,
  shapeHauntedKeep,
} from './resizeTable';

export interface BuiltInLevel {
  id: string;
  name: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  subtitle: string;
  objective: string;
  table: TableDefinition;
}

function stabilizeBuiltInGeometry(table: TableDefinition): TableDefinition {
  for (const id of ['post-left-inner', 'post-right-inner']) {
    const part = table.parts.find((candidate) => candidate.id === id);
    if (part?.kind === 'post') part.collider = false;
  }
  const leftSling = table.parts.find((part) => part.id === 'sling-left');
  if (leftSling?.kind === 'slingshot') {
    leftSling.points = [{ x: 12.5, y: 76.5 }, { x: 18.1, y: 82.6 }, { x: 12.9, y: 88.2 }];
  }
  const rightSling = table.parts.find((part) => part.id === 'sling-right');
  if (rightSling?.kind === 'slingshot') {
    rightSling.points = [{ x: 39.5, y: 76.5 }, { x: 33.9, y: 82.6 }, { x: 39.1, y: 88.2 }];
  }
  return table;
}

function cloneBase(id: string, name: string): TableDefinition {
  const clone = stabilizeBuiltInGeometry(JSON.parse(JSON.stringify(GRAND_CIRCUIT)) as TableDefinition);
  clone.id = id;
  clone.name = name;
  clone.provenance = { isOfficial: true, isPublished: true };
  clone.rules = undefined;
  return clone;
}

function bumper(table: TableDefinition, id: string): BumperPart | undefined {
  const part = table.parts.find((candidate) => candidate.id === id);
  return part?.kind === 'bumper' ? part : undefined;
}

function target(table: TableDefinition, id: string): TargetPart | undefined {
  const part = table.parts.find((candidate) => candidate.id === id);
  return part?.kind === 'target' ? part : undefined;
}

function withMachineArt(table: TableDefinition, machineArtId: string): TableDefinition {
  table.theme.machineArtId = machineArtId;
  return table;
}

function grandCircuit(): TableDefinition {
  const table = addCatchBonus(
    normalizePlayfieldShell(
      makeRampReadable(
        resizeTable(
          stabilizeBuiltInGeometry(JSON.parse(JSON.stringify(GRAND_CIRCUIT)) as TableDefinition),
          58,
          116,
        ),
      ),
    ),
    { id: 'catch-grand' },
  );
  table.rules = {
    comboWindowMs: 2300,
    comboStepBonus: 300,
    completionBonus: 15000,
    missions: [
      { id: 'gc-top-lanes', name: 'Light the Grid', description: 'Roll through three top lanes.', trigger: 'rollover', partIds: ['roll-a', 'roll-b', 'roll-c'], count: 3, bonus: 4000 },
      { id: 'gc-ramp', name: 'Grand Loop', description: 'Make the right ramp three times.', trigger: 'ramp', partIds: ['ramp-main'], count: 3, bonus: 8000 },
      { id: 'gc-pit', name: 'Pit Service', description: 'Take the pit stop before the finish-line bank opens.', trigger: 'kicker', partIds: ['catch-grand'], count: 1, bonus: 6000 },
      { id: 'gc-targets', name: 'Finish Line', description: 'Hit the center target bank three times.', trigger: 'target', partIds: ['target-1', 'target-2', 'target-3'], count: 3, bonus: 10000 },
    ],
  };
  return table;
}

function candyCarnival(): TableDefinition {
  let table = cloneBase('candy-carnival-v1', 'Candy Carnival');
  table.board.ballSaverSeconds = 15;
  table.board.inclineDeg = 5.8;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 17, y: 31 }; left.radius = 3.5; left.impulse = 54; }
  if (right) { right.center = { x: 35, y: 31 }; right.radius = 3.5; right.impulse = 54; }
  if (center) { center.center = { x: 26, y: 42 }; center.radius = 3.7; center.impulse = 58; }
  table = addCatchBonus(normalizePlayfieldShell(makeRampReadable(resizeTable(table, 62, 112))), { id: 'catch-candy' });
  table.rules = {
    comboWindowMs: 3200,
    comboStepBonus: 150,
    completionBonus: 18000,
    missions: [
      { id: 'candy-booth', name: 'Open the Prize Booth', description: 'Trap the ball in Sugar Rush to light the hidden center jackpot.', trigger: 'kicker', partIds: ['catch-candy'], count: 1, bonus: 4000 },
      { id: 'candy-jackpot', name: 'Crack the Sugar Jackpot', description: 'Drop the exposed center jackpot before the sugar surge fades.', trigger: 'target', partIds: ['target-2'], count: 1, bonus: 7000 },
      { id: 'candy-prizes', name: 'Sweep the Side Prizes', description: 'Collect both prizes released by the Sugar Jackpot.', trigger: 'target', partIds: ['target-1', 'target-3'], count: 2, bonus: 6000 },
      { id: 'candy-pops', name: 'Candy Frenzy', description: 'Celebrate with six pop-bumper hits.', trigger: 'bumper', count: 6, bonus: 4500 },
      { id: 'candy-ramp', name: 'Candy Slide', description: 'Ride the ramp twice to close the prize run.', trigger: 'ramp', partIds: ['ramp-main'], count: 2, bonus: 6000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'candy-chaos'), 'candy-carnival');
  return applySoundPack(table, 'silly');
}

function cosmicRelay(): TableDefinition {
  let table = cloneBase('cosmic-relay-v1', 'Cosmic Relay');
  table.board.inclineDeg = 6.6;
  table.board.ballSaverSeconds = 12;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 16, y: 27 }; left.impulse = 62; }
  if (right) { right.center = { x: 34, y: 25 }; right.impulse = 64; }
  if (center) { center.center = { x: 25, y: 38 }; center.impulse = 68; }
  const t1 = target(table, 'target-1');
  const t2 = target(table, 'target-2');
  const t3 = target(table, 'target-3');
  if (t1) t1.center = { x: 14, y: 54 };
  if (t2) t2.center = { x: 25, y: 52 };
  if (t3) t3.center = { x: 36, y: 54 };
  table = addCatchBonus(normalizePlayfieldShell(makeRampReadable(resizeTable(table, 66, 128))), { id: 'catch-cosmic' });
  table.rules = {
    comboWindowMs: 2600,
    comboStepBonus: 350,
    completionBonus: 18000,
    missions: [
      { id: 'cosmic-uplink', name: 'Establish Uplink', description: 'Enter Relay Surge to power the first relay node.', trigger: 'kicker', partIds: ['catch-cosmic'], count: 1, bonus: 5000 },
      { id: 'cosmic-relay-alpha', name: 'Acquire Relay Alpha', description: 'Lock the first exposed relay before the surge decays.', trigger: 'target', partIds: ['target-1'], count: 1, bonus: 3000 },
      { id: 'cosmic-relay-beta', name: 'Bridge Relay Beta', description: 'Carry the uplink into the newly revealed second relay.', trigger: 'target', partIds: ['target-2'], count: 1, bonus: 4500 },
      { id: 'cosmic-relay-gamma', name: 'Transmit Relay Gamma', description: 'Finish the chain through the final high-value relay.', trigger: 'target', partIds: ['target-3'], count: 1, bonus: 6500 },
      { id: 'cosmic-ramp', name: 'Slingshot Orbit', description: 'Complete two ramp relays.', trigger: 'ramp', partIds: ['ramp-main'], count: 2, bonus: 7000 },
      { id: 'cosmic-pops', name: 'Meteor Storm', description: 'Survive six pop impacts.', trigger: 'bumper', count: 6, bonus: 7500 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'cosmic-voyage'), 'cosmic-relay');
  return applySoundPack(table, 'deep-space');
}

function neonRush(): TableDefinition {
  let table = cloneBase('neon-rush-v1', 'Neon Rush');
  table.board.inclineDeg = 7.1;
  table.board.ballSaverSeconds = 10;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 18, y: 26 }; left.radius = 2.8; left.impulse = 72; }
  if (right) { right.center = { x: 34, y: 29 }; right.radius = 2.8; right.impulse = 74; }
  if (center) { center.center = { x: 26, y: 38 }; center.radius = 3; center.impulse = 78; }
  table = addCatchBonus(normalizePlayfieldShell(makeRampReadable(resizeTable(table, 56, 136))), { id: 'catch-neon' });
  table.rules = {
    comboWindowMs: 1800,
    comboStepBonus: 500,
    completionBonus: 22000,
    missions: [
      { id: 'neon-pops', name: 'Charge Reactor', description: 'Hammer eight hot pops.', trigger: 'bumper', count: 8, bonus: 7000 },
      { id: 'neon-ramp', name: 'Overclock', description: 'Make four ramp shots.', trigger: 'ramp', partIds: ['ramp-main'], count: 4, bonus: 13000 },
      { id: 'neon-targets', name: 'Break the Limit', description: 'Hit four stand-up targets.', trigger: 'target', count: 4, bonus: 15000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'neon-reactor'), 'neon-rush');
  return applySoundPack(table, 'neon-future');
}

function hauntedKeep(): TableDefinition {
  let table = cloneBase('haunted-keep-v1', 'Haunted Keep');
  table.board.inclineDeg = 6.8;
  table.board.ballSaverSeconds = 12;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 17, y: 30 }; left.impulse = 58; }
  if (right) { right.center = { x: 35, y: 30 }; right.impulse = 58; }
  if (center) { center.center = { x: 26, y: 44 }; center.impulse = 61; }
  const t1 = target(table, 'target-1');
  const t2 = target(table, 'target-2');
  const t3 = target(table, 'target-3');
  if (t1) { t1.center = { x: 18, y: 55 }; t1.scoreValue = 800; t1.hitPoints = 2; t1.destroyScoreValue = 1800; t1.creatureRef = 'stone-wall'; t1.appearsAfterPartId = 'catch-haunted'; }
  if (t2) { t2.center = { x: 26, y: 53 }; t2.scoreValue = 1000; t2.hitPoints = 3; t2.destroyScoreValue = 3500; t2.creatureRef = 'ghost'; t2.appearsAfterPartId = 'target-1'; }
  if (t3) { t3.center = { x: 34, y: 55 }; t3.scoreValue = 900; t3.hitPoints = 2; t3.destroyScoreValue = 2400; t3.creatureRef = 'gargoyle'; t3.appearsAfterPartId = 'target-2'; }
  table = addCatchBonus(normalizePlayfieldShell(shapeHauntedKeep(addUpperRamp(addUpperFlippers(makeRampReadable(resizeTable(table, 68, 172)))))), { id: 'catch-haunted', scoreValue: 1200, holdMs: 850, radius: 3.1 });
  table.rules = {
    comboWindowMs: 2700,
    comboStepBonus: 350,
    completionBonus: 34000,
    missions: [
      { id: 'haunt-awakening', name: 'Awaken the Keep', description: 'Enter the spirit catch to awaken the castle barricade.', trigger: 'kicker', partIds: ['catch-haunted'], count: 1, bonus: 6000 },
      { id: 'haunt-wall', name: 'Breach the Keep', description: 'Destroy the awakened stone barricade.', trigger: 'prop-destroy', partIds: ['target-1'], count: 1, bonus: 7000 },
      { id: 'haunt-ghost', name: 'Banish the Ghost', description: 'Destroy the center ghost.', trigger: 'prop-destroy', partIds: ['target-2'], count: 1, bonus: 11000 },
      { id: 'haunt-gargoyle', name: 'Slay the Gargoyle', description: 'Wake and destroy the gargoyle.', trigger: 'prop-destroy', partIds: ['target-3'], count: 1, bonus: 16000 },
      { id: 'haunt-breach-route', name: 'Break the Portcullis', description: 'Punch through the lower castle ramp once.', trigger: 'ramp', partIds: ['ramp-main'], count: 1, bonus: 9000 },
      { id: 'haunt-upper-escape', name: 'Flee the Battlements', description: 'Reach the upper loop to escape the keep.', trigger: 'ramp', partIds: ['ramp-upper-loop'], count: 1, bonus: 13000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'haunted-mansion'), 'haunted-keep');
  return applySoundPack(table, 'haunted-arcade');
}

function jungleTemple(): TableDefinition {
  let table = cloneBase('jungle-temple-v1', 'Jungle Temple');
  table.board.inclineDeg = 6.25;
  table.board.ballSaverSeconds = 13;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  // Relic Chamber already requires all three idols; make each required contact
  // a different decision instead of three near-identical pops. The broad left
  // idol is the control catch, the center is the balanced transfer, and the
  // compact right idol is the high-energy/high-value risk shot.
  if (left) { left.center = { x: 15, y: 30 }; left.radius = 3.8; left.impulse = 42; left.scoreValue = 850; }
  if (right) { right.center = { x: 37, y: 34 }; right.radius = 2.8; right.impulse = 82; right.scoreValue = 1800; }
  if (center) { center.center = { x: 26, y: 44 }; center.radius = 3.2; center.impulse = 62; center.scoreValue = 1200; }
  const t1 = target(table, 'target-1');
  const t2 = target(table, 'target-2');
  const t3 = target(table, 'target-3');
  if (t1) { t1.center = { x: 15, y: 58 }; t1.creatureRef = 'stone-wall'; t1.hitPoints = 2; t1.scoreValue = 900; t1.destroyScoreValue = 2200; t1.appearsAfterPartId = 'target-2'; }
  if (t2) { t2.center = { x: 26, y: 55 }; t2.creatureRef = 'crystal'; t2.hitPoints = 2; }
  if (t3) { t3.center = { x: 37, y: 58 }; t3.creatureRef = 'gargoyle'; t3.hitPoints = 2; t3.scoreValue = 1100; t3.destroyScoreValue = 3000; t3.appearsAfterPartId = 'target-2'; }
  table = addCatchBonus(normalizePlayfieldShell(makeRampReadable(resizeTable(table, 64, 132))), { id: 'catch-jungle', scoreValue: 1000, holdMs: 720, radius: 3 });
  table.rules = {
    comboWindowMs: 2850,
    comboStepBonus: 300,
    completionBonus: 30000,
    missions: [
      { id: 'jungle-catch', name: 'Claim the Relic', description: 'Enter the relic catch to open the crystal chamber.', trigger: 'kicker', partIds: ['catch-jungle'], count: 1, bonus: 6000 },
      { id: 'jungle-crystal', name: 'Shatter the Heart', description: 'Destroy the revealed crystal at the heart of the temple.', trigger: 'prop-destroy', partIds: ['target-2'], count: 1, bonus: 11000 },
      { id: 'jungle-guardians', name: 'Release the Guardians', description: 'Destroy both temple guardians awakened by the shattered crystal.', trigger: 'prop-destroy', partIds: ['target-1', 'target-3'], count: 2, bonus: 9000 },
      { id: 'jungle-waterfall', name: 'Waterfall Run', description: 'Climb the temple ramp twice.', trigger: 'ramp', partIds: ['ramp-main'], count: 2, bonus: 9000 },
      { id: 'jungle-relics', name: 'Relic Chamber', description: 'Wake all three relic idols once.', trigger: 'bumper', partIds: ['bumper-left', 'bumper-center', 'bumper-right'], count: 3, bonus: 9500 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'jungle-ruins'), 'jungle-temple');
  return applySoundPack(table, 'royal-fantasy');
}

function deepSeaDive(): TableDefinition {
  let table = cloneBase('deep-sea-dive-v1', 'Deep Sea Dive');
  table.board.inclineDeg = 5.95;
  table.board.ballSaverSeconds = 14;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 18, y: 27 }; left.radius = 3.6; left.impulse = 52; }
  if (right) { right.center = { x: 39, y: 28 }; right.radius = 3.6; right.impulse = 52; }
  if (center) { center.center = { x: 29, y: 41 }; center.radius = 4; center.impulse = 55; }
  table = addCatchBonus(normalizePlayfieldShell(makeRampReadable(resizeTable(table, 70, 126))), { id: 'catch-deep', scoreValue: 1400, holdMs: 980, radius: 3.5 });
  table.rules = {
    comboWindowMs: 3500,
    comboStepBonus: 220,
    completionBonus: 25000,
    missions: [
      { id: 'deep-lanes', name: 'Sonar Sweep', description: 'Scan all three upper lanes.', trigger: 'rollover', partIds: ['roll-a', 'roll-b', 'roll-c'], count: 3, bonus: 6500 },
      { id: 'deep-pops', name: 'Reef Rescue', description: 'Bounce through ten reef bumpers.', trigger: 'bumper', count: 10, bonus: 7000 },
      { id: 'deep-catch', name: 'Deep Catch', description: 'Enter the decompression catch to expose the pressure lock.', trigger: 'kicker', partIds: ['catch-deep'], count: 1, bonus: 5000 },
      { id: 'deep-pressure', name: 'Pressure Lock', description: 'Break the revealed center pressure lock.', trigger: 'target', partIds: ['target-2'], count: 1, bonus: 8500 },
      { id: 'deep-dive', name: 'Trench Dive', description: 'Complete the long trench ramp once.', trigger: 'ramp', partIds: ['ramp-main'], count: 1, bonus: 10000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'ocean-depths'), 'deep-sea-dive');
  return applySoundPack(table, 'solid-state-80s');
}

function clockworkDistrict(): TableDefinition {
  let table = cloneBase('clockwork-district-v1', 'Clockwork District');
  table.board.inclineDeg = 6.9;
  table.board.ballSaverSeconds = 11;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 16, y: 30 }; left.radius = 2.9; left.impulse = 56; left.scoreValue = 700; }
  if (right) { right.center = { x: 36, y: 27 }; right.radius = 2.9; right.impulse = 67; right.scoreValue = 950; }
  if (center) { center.center = { x: 26, y: 41 }; center.radius = 3.1; center.impulse = 80; center.scoreValue = 1450; }
  const t2 = target(table, 'target-2');
  if (t2) {
    t2.creatureRef = 'robot';
    t2.hitPoints = 3;
    t2.scoreValue = 1200;
    t2.destroyScoreValue = 5200;
    t2.appearsAfterPartId = 'catch-clockwork';
  }
  table = addCatchBonus(normalizePlayfieldShell(addUpperRamp(makeRampReadable(resizeTable(table, 60, 144)))), { id: 'catch-clockwork', scoreValue: 1250, holdMs: 650, radius: 2.8 });
  table.rules = {
    comboWindowMs: 2250,
    comboStepBonus: 425,
    completionBonus: 35000,
    missions: [
      { id: 'clock-winding', name: 'Wind the Key', description: 'Catch the ball in the winding station to release the automaton.', trigger: 'kicker', partIds: ['catch-clockwork'], count: 1, bonus: 5500 },
      { id: 'clock-robot', name: 'Break the Automaton', description: 'Destroy the released three-hit clockwork guardian.', trigger: 'prop-destroy', partIds: ['target-2'], count: 1, bonus: 8500 },
      { id: 'clock-mainspring', name: 'Charge the Mainspring', description: 'Drive the main mechanism ramp twice.', trigger: 'ramp', partIds: ['ramp-main'], count: 2, bonus: 6500 },
      { id: 'clock-escapement', name: 'Sync the Escapement', description: 'Climb the upper mechanism loop twice.', trigger: 'ramp', partIds: ['ramp-upper-loop'], count: 2, bonus: 7500 },
      { id: 'clock-governor', name: 'Balance the Governor', description: 'Touch all three governor weights to stabilize the district.', trigger: 'bumper', partIds: ['bumper-left', 'bumper-center', 'bumper-right'], count: 3, bonus: 9000 },
      { id: 'clock-release', name: 'Release the Escapement', description: 'Return to the upper loop once more to release the wound mechanism.', trigger: 'ramp', partIds: ['ramp-upper-loop'], count: 1, bonus: 10500 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'steampunk-lab'), 'clockwork-district');
  return applySoundPack(table, 'classic-mechanical');
}

function frontierRun(): TableDefinition {
  let table = cloneBase('frontier-run-v1', 'Frontier Run');
  table.board.inclineDeg = 6.45;
  table.board.ballSaverSeconds = 12;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  if (left) { left.center = { x: 14, y: 32 }; left.radius = 3; left.impulse = 61; }
  if (right) { right.center = { x: 38, y: 32 }; right.radius = 3; right.impulse = 61; }
  if (center) { center.center = { x: 26, y: 46 }; center.radius = 3.2; center.impulse = 64; }
  table = addCatchBonus(normalizePlayfieldShell(addUpperRamp(makeRampReadable(resizeTable(table, 64, 138)))), { id: 'catch-frontier', scoreValue: 1100, holdMs: 760, radius: 3 });
  table.rules = {
    comboWindowMs: 2500,
    comboStepBonus: 360,
    completionBonus: 24500,
    missions: [
      { id: 'frontier-targets', name: 'Wanted Posters', description: 'Clear the three outlaw targets.', trigger: 'target', partIds: ['target-1', 'target-2', 'target-3'], count: 3, bonus: 8500 },
      { id: 'frontier-rails', name: 'Rail Return', description: 'Ride either railway ramp three times.', trigger: 'ramp', partIds: ['ramp-main', 'ramp-upper-loop'], count: 3, bonus: 10500 },
      { id: 'frontier-mine', name: 'Blast the Gold Seam', description: 'Work the center mine bumper three times to break the ore loose.', trigger: 'bumper', partIds: ['bumper-center'], count: 3, bonus: 6000 },
      { id: 'frontier-freight', name: 'Load the Ore Cars', description: 'Route the haul through all three upper freight lanes.', trigger: 'rollover', partIds: ['roll-a', 'roll-b', 'roll-c'], count: 3, bonus: 10000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'desert-rally'), 'frontier-run');
  return applySoundPack(table, 'casino-night');
}

function dragonKeep(): TableDefinition {
  let table = cloneBase('dragon-keep-v1', 'Dragon Keep');
  table.board.inclineDeg = 7;
  table.board.ballSaverSeconds = 11;
  const left = bumper(table, 'bumper-left');
  const right = bumper(table, 'bumper-right');
  const center = bumper(table, 'bumper-center');
  // Keep the side braziers as recoverable scoring contacts while turning the
  // central furnace into a true fire-vent hazard. The volatile center kick is
  // intentionally worth very little, so skilled play routes around it instead
  // of farming the hottest rebound on an already fast four-flipper table.
  if (left) { left.center = { x: 17, y: 29 }; left.radius = 3.1; left.impulse = 56; left.scoreValue = 900; }
  if (right) { right.center = { x: 35, y: 29 }; right.radius = 3.1; right.impulse = 56; right.scoreValue = 900; }
  if (center) { center.center = { x: 26, y: 43 }; center.radius = 3.6; center.impulse = 94; center.scoreValue = 150; }
  const t1 = target(table, 'target-1');
  const t2 = target(table, 'target-2');
  const t3 = target(table, 'target-3');
  if (t1) { t1.creatureRef = 'stone-wall'; t1.hitPoints = 2; t1.destroyScoreValue = 3000; }
  if (t2) { t2.creatureRef = 'crystal'; t2.hitPoints = 3; t2.destroyScoreValue = 5000; }
  if (t3) { t3.creatureRef = 'gargoyle'; t3.hitPoints = 3; t3.destroyScoreValue = 6000; t3.appearsAfterPartId = 'target-2'; }
  table = addCatchBonus(normalizePlayfieldShell(addUpperRamp(addUpperFlippers(makeRampReadable(resizeTable(table, 68, 158))))), { id: 'catch-dragon', scoreValue: 1500, holdMs: 720, radius: 3.1 });
  table.rules = {
    comboWindowMs: 2050,
    comboStepBonus: 550,
    completionBonus: 36000,
    missions: [
      { id: 'dragon-gate', name: 'Break the Gate', description: 'Destroy the keep barricade.', trigger: 'prop-destroy', partIds: ['target-1'], count: 1, bonus: 8500 },
      { id: 'dragon-crystal', name: 'Claim the Fire Crystal', description: 'Shatter the central crystal.', trigger: 'prop-destroy', partIds: ['target-2'], count: 1, bonus: 12000 },
      { id: 'dragon-guardian', name: 'Defeat the Guardian', description: 'Wake and defeat the final guardian.', trigger: 'prop-destroy', partIds: ['target-3'], count: 1, bonus: 15000 },
      { id: 'dragon-rails', name: 'Dragon Flight', description: 'Complete either fiery ramp four times.', trigger: 'ramp', partIds: ['ramp-main', 'ramp-upper-loop'], count: 4, bonus: 17000 },
    ],
  };
  table = withMachineArt(applyVisualPack(table, 'volcanic-core'), 'dragon-keep');
  return applySoundPack(table, 'royal-fantasy');
}

function official(table: TableDefinition): TableDefinition {
  return rebuildTableFlow(normalizeBuiltInHardware(table));
}

export const BUILT_IN_LEVELS: BuiltInLevel[] = [
  { id: 'grand-circuit', name: 'Grand Circuit', difficulty: 'Medium', subtitle: 'The flagship Pin starter machine: broad, readable and balanced.', objective: 'Light the Grid, loop the ramp, take pit service and finish the target bank.', table: official(grandCircuit()) },
  { id: 'candy-carnival', name: 'Candy Carnival', difficulty: 'Easy', subtitle: 'A wide, playful cabinet with big pops and generous recovery.', objective: 'Open the prize booth, crack the Sugar Jackpot, sweep both prizes and ride the Candy Slide.', table: official(candyCarnival()) },
  { id: 'cosmic-relay', name: 'Cosmic Relay', difficulty: 'Medium', subtitle: 'A large space table built for open orbit shots and relay combos.', objective: 'Establish the uplink, carry it through Alpha, Beta and Gamma, sling through orbit and survive the meteor storm.', table: official(cosmicRelay()) },
  { id: 'neon-rush', name: 'Neon Rush', difficulty: 'Hard', subtitle: 'A narrow, long speed table with hot pops and tight recovery windows.', objective: 'Charge the reactor, overclock the ramp and break the limit.', table: official(neonRush()) },
  { id: 'haunted-keep', name: 'Haunted Keep', difficulty: 'Hard', subtitle: 'An extra-long gothic machine with an upper battle zone and four flippers.', objective: 'Awaken the keep, defeat its guardians, breach the lower portcullis and escape across the upper battlements.', table: official(hauntedKeep()) },
  { id: 'jungle-temple', name: 'Jungle Temple', difficulty: 'Medium', subtitle: 'A relic-hunting adventure with broad recoveries and destructible guardians.', objective: 'Claim the relic, shatter the chamber crystal, defeat both awakened guardians, climb the waterfall and awaken all three relic idols.', table: official(jungleTemple()) },
  { id: 'deep-sea-dive', name: 'Deep Sea Dive', difficulty: 'Easy', subtitle: 'A flowing, forgiving ocean machine with large reef pops and long combos.', objective: 'Sweep sonar lanes, rescue the reef, take the deep catch, breach the pressure lock and dive the trench.', table: official(deepSeaDive()) },
  { id: 'clockwork-district', name: 'Clockwork District', difficulty: 'Hard', subtitle: 'A mechanical two-ramp machine with sharp rebounds and fast pressure cycles.', objective: 'Wind the key, break the automaton, charge the mainspring, sync the escapement, balance the governor and release the mechanism.', table: official(clockworkDistrict()) },
  { id: 'frontier-run', name: 'Frontier Run', difficulty: 'Medium', subtitle: 'A railway chase with two ramp routes, target banks and jackpot-style audio.', objective: 'Round up the posse, chase the outlaws, run the railway, blast the gold seam and load the ore train.', table: official(frontierRun()) },
  { id: 'dragon-keep', name: 'Dragon Keep', difficulty: 'Hard', subtitle: 'A fiery four-flipper boss machine with destructible guardians and two ramps.', objective: 'Break the gate, claim the crystal, defeat the guardian and complete Dragon Flight.', table: official(dragonKeep()) },
];

export function builtInLevelForTable(table: TableDefinition): BuiltInLevel | undefined {
  return BUILT_IN_LEVELS.find((level) => level.table.id === table.id);
}
